import { forwardRef, Inject, Injectable } from '@angular/core';
import { DateRange } from '@angular/material/datepicker';
import * as moment from 'moment';
import * as moment_tz from 'moment-timezone';
import { LOCALE_TIMEZONE } from '../common/config';
import { DatetimeFormatterPipe } from '../shared/pipe';
import { AuthenticationService } from './authentication.service';
import { ContextService } from './context.service';

@Injectable()
export class DateRangeService {

    constructor(
        @Inject(forwardRef(() => DatetimeFormatterPipe)) private datetimeFormatterPipe: DatetimeFormatterPipe,
        @Inject(forwardRef(() => AuthenticationService)) private authService: AuthenticationService,
        @Inject(forwardRef(() => ContextService)) private contextService: ContextService
    ) {
    }

    getAllDateRanges(): CustomDateRange[] {
        return Object.keys(DateRangeName).map(name => this.getCustomDateRangeByName(name));
    }

    getCustomDateRangeByName(name: string): CustomDateRange {
        const timezone = this.contextService.getTimezone();
        const currentQuarter = moment_tz.tz(timezone || LOCALE_TIMEZONE).quarter();
        switch (name) {
            case DateRangeName.TODAY:
                return { name: name, label: "todayProperty", range: new DateRange(moment_tz.tz(timezone || LOCALE_TIMEZONE).startOf('day'), moment_tz.tz(timezone || LOCALE_TIMEZONE)) };
            case DateRangeName.YESTERDAY:
                return { name: name, label: "yesterdayProperty", range: new DateRange(moment_tz.tz(timezone || LOCALE_TIMEZONE).subtract(1, 'days').startOf('day'), moment_tz.tz(timezone || LOCALE_TIMEZONE).subtract(1, 'days').endOf('day')) };
            case DateRangeName.LAST_1_HOUR:
                return { name: name, label: "last1HourProperty", range: new DateRange(moment_tz.tz(timezone || LOCALE_TIMEZONE).subtract(1, 'hours'), moment_tz.tz(timezone || LOCALE_TIMEZONE)) };
            case DateRangeName.LAST_2_HOURS:
                return { name: name, label: "last2HoursProperty", range: new DateRange(moment_tz.tz(timezone || LOCALE_TIMEZONE).subtract(2, 'hours'), moment_tz.tz(timezone || LOCALE_TIMEZONE)) };
            case DateRangeName.LAST_6_HOURS:
                return { name: name, label: "last6HoursProperty", range: new DateRange(moment_tz.tz(timezone || LOCALE_TIMEZONE).subtract(6, 'hours'), moment_tz.tz(timezone || LOCALE_TIMEZONE)) };
            case DateRangeName.LAST_12_HOURS:
                return { name: name, label: "last12HoursProperty", range: new DateRange(moment_tz.tz(timezone || LOCALE_TIMEZONE).subtract(12, 'hours'), moment_tz.tz(timezone || LOCALE_TIMEZONE)) };
            case DateRangeName.LAST_24_HOURS:
                return { name: name, label: "last24HoursProperty", range: new DateRange(moment_tz.tz(timezone || LOCALE_TIMEZONE).subtract(24, 'hours'), moment_tz.tz(timezone || LOCALE_TIMEZONE)) };
            case DateRangeName.LAST_7_DAYS:
                return { name: name, label: "last7DaysProperty", range: new DateRange(moment_tz.tz(timezone || LOCALE_TIMEZONE).subtract(7, 'days'), moment_tz.tz(timezone || LOCALE_TIMEZONE)) };
            case DateRangeName.LAST_FULL_7_DAYS:
                return { name: name, label: "last7DaysProperty", range: new DateRange(moment_tz.tz(timezone || LOCALE_TIMEZONE).subtract(7, 'days').startOf('day'), moment_tz.tz(timezone || LOCALE_TIMEZONE)) };
            case DateRangeName.THIS_WEEK:
                return { name: name, label: "thisWeekProperty", range: new DateRange(moment_tz.tz(timezone || LOCALE_TIMEZONE).startOf('isoWeek'), moment_tz.tz(timezone || LOCALE_TIMEZONE)) };
            case DateRangeName.LAST_WEEK:
                return { name: name, label: "lastWeekProperty", range: new DateRange(moment_tz.tz(timezone || LOCALE_TIMEZONE).subtract(7, 'days').startOf('isoWeek'), moment_tz.tz(timezone || LOCALE_TIMEZONE).subtract(7, 'days').endOf('isoWeek')) };
            case DateRangeName.LAST_30_DAYS:
                return { name: name, label: "last30DaysProperty", range: new DateRange(moment_tz.tz(timezone || LOCALE_TIMEZONE).subtract(30, 'days'), moment_tz.tz(timezone || LOCALE_TIMEZONE)) };
            case DateRangeName.LAST_FULL_30_DAYS:
                return { name: name, label: "last30DaysProperty", range: new DateRange(moment_tz.tz(timezone || LOCALE_TIMEZONE).subtract(30, 'days').startOf('day'), moment_tz.tz(timezone || LOCALE_TIMEZONE)) };
            case DateRangeName.THIS_MONTH:
                return { name: name, label: "currentMonthProperty", range: new DateRange(moment_tz.tz(timezone || LOCALE_TIMEZONE).startOf('month'), moment_tz.tz(timezone || LOCALE_TIMEZONE)) };
            case DateRangeName.LAST_MONTH:
                return { name: name, label: "lastMonthProperty", range: new DateRange(moment_tz.tz(timezone || LOCALE_TIMEZONE).subtract(1, 'month').startOf('month'), moment_tz.tz(timezone || LOCALE_TIMEZONE).subtract(1, 'month').endOf('month')) };
            case DateRangeName.LAST_6_MONTHS:
                return { name: name, label: "last6MonthsProperty", range: new DateRange(moment_tz.tz(timezone || LOCALE_TIMEZONE).subtract(6, 'months'), moment_tz.tz(timezone || LOCALE_TIMEZONE)) };
            case DateRangeName.LAST_12_MONTHS:
                return { name: name, label: "last12MonthsProperty", range: new DateRange(moment_tz.tz(timezone || LOCALE_TIMEZONE).subtract(12, 'months'), moment_tz.tz(timezone || LOCALE_TIMEZONE)) };
            case DateRangeName.LAST_FULL_12_MONTHS:
                return { name: name, label: "last12MonthsProperty", range: new DateRange(moment_tz.tz(timezone || LOCALE_TIMEZONE).subtract(12, 'months').startOf('day'), moment_tz.tz(timezone || LOCALE_TIMEZONE)) };
            case DateRangeName.THIS_YEAR:
                return { name: name, label: "thisYearProperty", range: new DateRange(moment_tz.tz(timezone || LOCALE_TIMEZONE).startOf('year'), moment_tz.tz(timezone || LOCALE_TIMEZONE)) };
            case DateRangeName.LAST_YEAR:
                return { name: name, label: "lastYearProperty", range: new DateRange(moment_tz.tz(timezone || LOCALE_TIMEZONE).subtract(1, 'years').startOf('year'), moment_tz.tz(timezone || LOCALE_TIMEZONE).subtract(1, 'years').endOf('year')) };
            case DateRangeName.LAST_FULL_3_MONTHS:
                return { name: name, label: "last3MonthsProperty", range: new DateRange(moment_tz.tz(timezone || LOCALE_TIMEZONE).subtract(3, 'months').startOf('day'), moment_tz.tz(timezone || LOCALE_TIMEZONE)) };
            case DateRangeName.LAST_FULL_6_MONTHS:
                return { name: name, label: "last6MonthsProperty", range: new DateRange(moment_tz.tz(timezone || LOCALE_TIMEZONE).subtract(6, 'months').startOf('day'), moment_tz.tz(timezone || LOCALE_TIMEZONE)) };
            case DateRangeName.THIS_QUARTER:
                return { name: name, label: "currentQuarterProperty", range: new DateRange(moment_tz.tz(timezone || LOCALE_TIMEZONE).startOf('quarter'), moment_tz.tz(timezone || LOCALE_TIMEZONE)) };
            case DateRangeName.LAST_QUARTER:
                return { name: name, label: "lastQuarterProperty", range: new DateRange(moment_tz.tz(timezone || LOCALE_TIMEZONE).subtract(1, 'quarter').startOf('quarter'), moment_tz.tz(timezone || LOCALE_TIMEZONE).subtract(1, 'quarter').endOf('quarter')) };
            case DateRangeName.THIS_SEMESTER:
                return { name: name, label: "currentSemesterProperty", range: new DateRange(moment_tz.tz(timezone || LOCALE_TIMEZONE).subtract((currentQuarter == 1 || currentQuarter == 3) ? 0 : 1, 'quarter').startOf('quarter'), moment_tz.tz(timezone || LOCALE_TIMEZONE)) };
            case DateRangeName.LAST_SEMESTER:
                return { name: name, label: "lastSemesterProperty", range: new DateRange(moment_tz.tz(timezone || LOCALE_TIMEZONE).subtract((currentQuarter == 1 || currentQuarter == 3) ? 2 : 3, 'quarter').startOf('quarter'), moment_tz.tz(timezone || LOCALE_TIMEZONE).subtract((currentQuarter == 1 || currentQuarter == 3) ? 1 : 2, 'quarter').endOf('quarter')) };
            default:
                console.error('Invalid range');
                return null;
        }
    }

    getPeriod(start: number, end: number): string {
        const timezone = this.contextService.getTimezone();
        const currentQuarter = moment_tz.tz(timezone || LOCALE_TIMEZONE).quarter();
        if (moment().valueOf() - end <= (1000 * 60 * 10) && end <= moment().valueOf()) { // end less than 10 minutes from now
            if (this.getStartTimestampByName(DateRangeName.TODAY) == start) {
                return this.getCustomDateRangeByName(DateRangeName.TODAY).label;
            } else if (moment(end).subtract(24, 'hours').valueOf() == start) {
                return this.getCustomDateRangeByName(DateRangeName.LAST_24_HOURS).label;
            } else if (moment(end).subtract(7, 'days').valueOf() == start) {
                return this.getCustomDateRangeByName(DateRangeName.LAST_7_DAYS).label;
            } else if (moment(end).subtract(7, 'days').startOf('day').valueOf() == start) {
                return this.getCustomDateRangeByName(DateRangeName.LAST_FULL_7_DAYS).label;
            } else if (moment(end).startOf('isoWeek').valueOf() == start) {
                return this.getCustomDateRangeByName(DateRangeName.THIS_WEEK).label;
            } else if (moment(end).subtract(30, 'days').valueOf() == start) {
                return this.getCustomDateRangeByName(DateRangeName.LAST_30_DAYS).label;
            } else if (moment(end).subtract(30, 'days').startOf('day').valueOf() == start) {
                return this.getCustomDateRangeByName(DateRangeName.LAST_FULL_30_DAYS).label;
            } else if (moment(end).startOf('month').valueOf() == start) {
                return this.getCustomDateRangeByName(DateRangeName.THIS_MONTH).label;
            } else if (moment(end).subtract(12, 'months').valueOf() == start) {
                return this.getCustomDateRangeByName(DateRangeName.LAST_12_MONTHS).label;
            } else if (moment(end).subtract(12, 'months').startOf('day').valueOf() == start) {
                return this.getCustomDateRangeByName(DateRangeName.LAST_FULL_12_MONTHS).label;
            } else if (moment(end).subtract(3, 'months').startOf('day').valueOf() == start) {
                return this.getCustomDateRangeByName(DateRangeName.LAST_FULL_3_MONTHS).label;
            } else if (moment(end).subtract(6, 'months').startOf('day').valueOf() == start) {
                return this.getCustomDateRangeByName(DateRangeName.LAST_FULL_6_MONTHS).label;
            } else if (moment(end).startOf('quarter').valueOf() == start) {
                return this.getCustomDateRangeByName(DateRangeName.THIS_QUARTER).label;
            } else if (moment(end).subtract((currentQuarter == 1 || currentQuarter == 3) ? 0 : 1, 'quarter').startOf('quarter').valueOf() == start) {
                return this.getCustomDateRangeByName(DateRangeName.THIS_SEMESTER).label;
            } else if (moment(end).startOf('year').valueOf() == start) {
                return this.getCustomDateRangeByName(DateRangeName.THIS_YEAR).label;
            }
        }
        if (this.getStartTimestampByName(DateRangeName.YESTERDAY) == start && this.getEndTimestampByName(DateRangeName.YESTERDAY) - (1000 * 60) <= end && end <= this.getEndTimestampByName(DateRangeName.YESTERDAY)) {
            return this.getCustomDateRangeByName(DateRangeName.YESTERDAY).label;
        } else if (this.getStartTimestampByName(DateRangeName.LAST_MONTH) == start && this.getEndTimestampByName(DateRangeName.LAST_MONTH) - (1000 * 60) <= end && end <= this.getEndTimestampByName(DateRangeName.LAST_MONTH)) {
            return this.getCustomDateRangeByName(DateRangeName.LAST_MONTH).label;
        } else if (this.getStartTimestampByName(DateRangeName.LAST_QUARTER) == start && this.getEndTimestampByName(DateRangeName.LAST_QUARTER) - (1000 * 60) <= end && end <= this.getEndTimestampByName(DateRangeName.LAST_QUARTER)) {
            return this.getCustomDateRangeByName(DateRangeName.LAST_QUARTER).label;
        } else if (this.getStartTimestampByName(DateRangeName.LAST_SEMESTER) == start && this.getEndTimestampByName(DateRangeName.LAST_SEMESTER) - (1000 * 60) <= end && end <= this.getEndTimestampByName(DateRangeName.LAST_SEMESTER)) {
            return this.getCustomDateRangeByName(DateRangeName.LAST_SEMESTER).label;
        } else if (start == moment(start).startOf('month').valueOf() && moment(start).endOf('month').subtract(1, 'minutes').valueOf() <= end && end <= moment(start).endOf('month').valueOf()) {
            if (moment().year == moment(start).year) {
                return this.datetimeFormatterPipe.transform(start, "MMMM");
            } else {
                return this.datetimeFormatterPipe.transform(start, "MMM YYYY");
            }
        } else if (moment(start).dayOfYear() == moment(end).dayOfYear() && moment().year == moment(start).year) {
            if (moment(end).valueOf() - moment(start).valueOf() < (1000 * 60 * 60 * 24) - (1000 * 60)) {
                if (moment(start).dayOfYear() == moment().dayOfYear()) {    //today
                    return this.datetimeFormatterPipe.transform(start, "HH:mm") + " - " + this.datetimeFormatterPipe.transform(end, "HH:mm");
                } else {
                    return this.datetimeFormatterPipe.transform(start, "DD MMM YYYY HH:mm") + " - " + this.datetimeFormatterPipe.transform(end, "HH:mm");
                }
            } else {
                return this.datetimeFormatterPipe.transform(start, "DD MMM YYYY");
            }
        }
        return this.datetimeFormatterPipe.transform(start, "DD MMM YYYY") + " - " + this.datetimeFormatterPipe.transform(end, "DD MMM YYYY");
    }

    private getStartTimestampByName(name: string): number {
        return this.getCustomDateRangeByName(name).range.start.valueOf();
    }

    private getEndTimestampByName(name: string): number {
        return this.getCustomDateRangeByName(name).range.end.valueOf();
    }

    getDefaultPeriods(): { start: moment.Moment, end: moment.Moment, label: string }[] {
        return Object.keys(DateRangeName).map(name => {
            const customDateRange = this.getCustomDateRangeByName(name);
            return { start: customDateRange.range.start, end: customDateRange.range.end, label: customDateRange.label, name: customDateRange.name };
        });
    }
}

export enum DateRangeName {
    TODAY = 'TODAY',
    YESTERDAY = 'YESTERDAY',
    LAST_1_HOUR = 'LAST_1_HOUR',
    LAST_2_HOURS = 'LAST_2_HOURS',
    LAST_6_HOURS = 'LAST_6_HOURS',
    LAST_12_HOURS = 'LAST_12_HOURS',
    LAST_24_HOURS = 'LAST_24_HOURS',
    LAST_7_DAYS = 'LAST_7_DAYS',
    LAST_FULL_7_DAYS = 'LAST_FULL_7_DAYS',
    THIS_WEEK = 'THIS_WEEK',
    LAST_WEEK = 'LAST_WEEK',
    LAST_30_DAYS = 'LAST_30_DAYS',
    LAST_FULL_30_DAYS = 'LAST_FULL_30_DAYS',
    THIS_MONTH = 'THIS_MONTH',
    LAST_MONTH = 'LAST_MONTH',
    LAST_6_MONTHS = 'LAST_6_MONTHS',
    LAST_12_MONTHS = 'LAST_12_MONTHS',
    LAST_FULL_12_MONTHS = 'LAST_FULL_12_MONTHS',
    THIS_YEAR = 'THIS_YEAR',
    LAST_YEAR = 'LAST_YEAR',
    LAST_FULL_3_MONTHS = 'LAST_FULL_3_MONTHS',
    LAST_FULL_6_MONTHS = 'LAST_FULL_6_MONTHS',
    THIS_QUARTER = 'THIS_QUARTER',
    LAST_QUARTER = 'LAST_QUARTER',
    THIS_SEMESTER = 'THIS_SEMESTER',
    LAST_SEMESTER = 'LAST_SEMESTER'
}

export class CustomDateRange {
    name: string;
    label: string;
    range: DateRange<moment.Moment>;
}

export class PeriodVariable {
    start: number;
    end: number;
    name: string;
}

export enum DateRangeGroup {
    HOURLY = 'HOURLY',
    DAILY = 'DAILY',
    WEEKLY = 'WEEKLY',
    MONTHLY = 'MONTHLY',
    YEARLY = 'YEARLY',
    QUARTERLY = 'QUARTERLY',
    SEMESTERLY = 'SEMESTERLY'
}

export const RANGE_MAPPING = new Map<DateRangeName, DateRangeGroup>([
    [DateRangeName.LAST_1_HOUR, DateRangeGroup.HOURLY],
    [DateRangeName.LAST_2_HOURS, DateRangeGroup.HOURLY],
    [DateRangeName.LAST_6_HOURS, DateRangeGroup.HOURLY],
    [DateRangeName.LAST_12_HOURS, DateRangeGroup.HOURLY],
    [DateRangeName.LAST_24_HOURS, DateRangeGroup.HOURLY],
    [DateRangeName.TODAY, DateRangeGroup.DAILY],
    [DateRangeName.YESTERDAY, DateRangeGroup.DAILY],
    [DateRangeName.LAST_7_DAYS, DateRangeGroup.DAILY],
    [DateRangeName.LAST_FULL_7_DAYS, DateRangeGroup.DAILY],
    [DateRangeName.LAST_30_DAYS, DateRangeGroup.DAILY],
    [DateRangeName.LAST_FULL_30_DAYS, DateRangeGroup.DAILY],
    [DateRangeName.THIS_WEEK, DateRangeGroup.WEEKLY],
    [DateRangeName.LAST_WEEK, DateRangeGroup.WEEKLY],
    [DateRangeName.THIS_MONTH, DateRangeGroup.MONTHLY],
    [DateRangeName.LAST_MONTH, DateRangeGroup.MONTHLY],
    [DateRangeName.LAST_6_MONTHS, DateRangeGroup.MONTHLY],
    [DateRangeName.LAST_12_MONTHS, DateRangeGroup.MONTHLY],
    [DateRangeName.LAST_FULL_12_MONTHS, DateRangeGroup.MONTHLY],
    [DateRangeName.THIS_YEAR, DateRangeGroup.YEARLY],
    [DateRangeName.LAST_YEAR, DateRangeGroup.YEARLY],
    [DateRangeName.LAST_FULL_3_MONTHS, DateRangeGroup.MONTHLY],
    [DateRangeName.LAST_FULL_6_MONTHS, DateRangeGroup.MONTHLY],
    [DateRangeName.THIS_QUARTER, DateRangeGroup.QUARTERLY],
    [DateRangeName.LAST_QUARTER, DateRangeGroup.QUARTERLY],
    [DateRangeName.THIS_SEMESTER, DateRangeGroup.SEMESTERLY],
    [DateRangeName.LAST_SEMESTER, DateRangeGroup.SEMESTERLY]
])

export const CUSTOM_RANGE = 'CUSTOM';